import axios from 'axios';
import { Call, CallDetailResponse, AnalyticsData, Workflow, Assistant } from '../types';

// Create axios instance with base configuration
const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000, // 10 second timeout
});

// Request interceptor for adding auth tokens
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear invalid token and redirect to login
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

/**
 * Fetch calls from the backend API - Direct database access, webhook-independent
 */
export const getCalls = async ({ skip = 0, limit = 20, search, }: { skip?: number; limit?: number; search?: string } = {}): Promise<Call[]> => {
  try {
    const response = await apiClient.get('/api/calls', {
      params: {
        skip,
        limit,
        search,
      },
    });

    // The API returns a direct array of CallSummarySchema objects from database
    return response.data;
  } catch (error: any) {
    console.error('Error fetching calls from database:', error);

    // Provide more specific error messages
    let errorMessage = 'Failed to fetch calls from database';

    if (error.response?.status === 401) {
      errorMessage = 'Authentication required - please log in';
    } else if (error.response?.status === 403) {
      errorMessage = 'Access denied - insufficient permissions';
    } else if (error.response?.status >= 500) {
      errorMessage = 'Database connection error - please try again';
    } else if (error.response?.data?.detail) {
      errorMessage = error.response.data.detail;
    }

    throw new Error(errorMessage);
  }
};

/**
 * Fetch a single call by ID from the backend API
 */
export const getCallById = async ({ callId }: { callId: string }): Promise<CallDetailResponse> => {
  try {
    const response = await apiClient.get(`/api/calls/${callId}`);

    return response.data;
  } catch (error: any) {
    console.error('Error fetching call details:', error);
    throw new Error(
      error.response?.data?.detail ||
      error.message ||
      'Failed to fetch call details'
    );
  }
};

/**
 * Fetch analytics data from the backend API
 */
export const getAnalytics = async (): Promise<AnalyticsData> => {
  try {
    const response = await apiClient.get('/api/analytics');

    return response.data;
  } catch (error: any) {
    console.error('Error fetching analytics:', error);
    throw new Error(
      error.response?.data?.detail ||
      error.message ||
      'Failed to fetch analytics'
    );
  }
};

/**
 * Login user with email and password
 */
export const loginUser = async (credentials: { email: string; password: string }): Promise<any> => {
  try {
    const formData = new URLSearchParams();
    formData.append('username', credentials.email);
    formData.append('password', credentials.password);

    const response = await apiClient.post('/auth/login', formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });

    return response.data;
  } catch (error: any) {
    console.error('Error logging in:', error);
    throw new Error(
      error.response?.data?.detail ||
      error.message ||
      'Login failed'
    );
  }
};

/**
 * Register a new user
 */
export const registerUser = async (userData: { name: string; email: string; password: string }): Promise<any> => {
  try {
    const response = await apiClient.post('/auth/register', userData);

    return response.data;
  } catch (error: any) {
    console.error('Error registering user:', error);
    throw new Error(
      error.response?.data?.detail ||
      error.message ||
      'Registration failed'
    );
  }
};

/**
 * Fetch workflows linked to the current user
 */
export const getWorkflows = async (): Promise<Workflow[]> => {
  try {
    const response = await apiClient.get('/api/workflows');

    return response.data;
  } catch (error: any) {
    console.error('Error fetching workflows:', error);
    throw new Error(
      error.response?.data?.detail ||
      error.message ||
      'Failed to fetch workflows'
    );
  }
};

/**
 * Link a new workflow to the current user
 */
export const linkWorkflow = async (workflowId: string, name?: string): Promise<Workflow> => {
  try {
    const response = await apiClient.post('/api/workflows', {
      vapi_workflow_id: workflowId,
      name: name || undefined
    });
    return response.data;
  } catch (error: any) {
    console.error('Error linking workflow:', error);
    throw new Error(
      error.response?.data?.detail ||
      error.message ||
      'Failed to link workflow'
    );
  }
};

/**
 * Fetch assistants linked to the current user
 */
export const getAssistants = async (
  search?: string
): Promise<Assistant[]> => {
  try {
    const response = await apiClient.get('/api/assistants', {
      params: search ? { search } : {}
    });

    return response.data;
  } catch (error: any) {
    console.error('Error fetching assistants:', error);
    throw new Error(
      error.response?.data?.detail ||
      error.message ||
      'Failed to fetch assistants'
    );
  }
};

/**
 * Link a new assistant to the current user
 */
export const linkAssistant = async (assistantData: { vapi_assistant_id: string; name?: string }): Promise<Assistant> => {
  try {
    const response = await apiClient.post('/api/assistants', assistantData);

    return response.data;
  } catch (error: any) {
    console.error('Error linking assistant:', error);
    throw new Error(
      error.response?.data?.detail ||
      error.message ||
      'Failed to link assistant'
    );
  }
};

/**
 * Unlink an assistant from the current user
 */
export const unlinkAssistant = async (assistantId: string): Promise<void> => {
  try {
    await apiClient.delete(`/api/assistants/${assistantId}`);
  } catch (error: any) {
    console.error('Error unlinking assistant:', error);
    throw new Error(
      error.response?.data?.detail ||
      error.message ||
      'Failed to unlink assistant'
    );
  }
};

/**
 * Unlink a workflow from the current user
 */
export const unlinkWorkflow = async (workflowId: string): Promise<void> => {
  try {
    await apiClient.delete(`/api/workflows/${workflowId}`);
  } catch (error: any) {
    console.error('Error unlinking workflow:', error);
    throw new Error(
      error.response?.data?.detail ||
      error.message ||
      'Failed to unlink workflow'
    );
  }
};

export default apiClient;